import { useAuth } from "@clerk/clerk-react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchParams } from "schemas/dashboard";
import { routes } from "schemas/routes";

import { fetcherAuth } from "./api";
import { isPage } from "./general";
import { getSource, identifyUser } from "./tracking";

export const useLogin = () => {
  const { setErrorAlert } = useContext(AlertContext);
  const [searchParams] = useSearchParams();
  const {
    currentOrg,
    setCurrentOrg,
    setCurrentUser,
    setProfile,
    gamificationProfile,
    setShowGamificationProfile,
  } = useContext(OrganizationUserContext);
  const { setSubscription } = useContext(SubscriptionContext);
  const referralCode = searchParams.get(SearchParams.REFERRAL_CODE);
  const affiliateCode = searchParams.get(SearchParams.AFFILIATE_CODE);
  const [fetchUserLoading, setFetchUserLoading] = useState<boolean>(false);

  const { getToken } = useAuth();

  const fetchUserInfo = async () => {
    try {
      setFetchUserLoading(true);
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/user?affiliate_code=${affiliateCode || ""}`,
      );
      if (res) {
        identifyUser(res.organizationUser.id);
        setCurrentOrg(res.organization);
        setSubscription(res.subscription);
        setProfile(res.organizationProfile);
      }
    } catch (error) {
      handleClerkUserSignUp();
    } finally {
      setFetchUserLoading(false);
    }
  };

  const handleClerkUserSignUp = async () => {
    const source = getSource();
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/signup`,
        "POST",
        {},
        {
          referralCode,
          affiliateCode,
          source: referralCode ? "referral" : source,
          isBrand: isPage(routes.onboardingBrands),
          isOOOFlow: isPage(routes.pitchPlease),
        },
      );
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setProfile(res.organizationProfile);
      setShowGamificationProfile(res.showGamificationProfile);
      if (
        // @ts-ignore
        typeof window.fbq !== "undefined" &&
        // @ts-ignore
        typeof window.fbq === "function"
      ) {
        // @ts-ignore
        window.fbq("track", "CompleteRegistration");
      }
      if (
        // @ts-ignore
        typeof window.ttq !== "undefined" &&
        // @ts-ignore
        typeof window.ttq.track === "function"
      ) {
        // @ts-ignore
        window.ttq.track("CompleteRegistration");
      }
      if (
        // @ts-ignore
        typeof window.oaiq !== "undefined" &&
        // @ts-ignore
        typeof window.oaiq === "function"
      ) {
        // @ts-ignore
        window.oaiq("measure", "registration_completed", {
          type: "customer_action",
        });
      }
      if (
        // @ts-ignore
        typeof window.gtag !== "undefined" &&
        // @ts-ignore
        typeof window.gtag === "function"
      ) {
        // @ts-ignore
        window.gtag("event", "conversion", {
          send_to: "AW-18111742383/0PfTCJuSlaIcEK-DrbxD",
        });
      }
      if (
        // @ts-ignore
        typeof window.lintrk === "function"
      ) {
        // @ts-ignore
        window.lintrk("track", { conversion_id: 28343778 });
      }
      setFetchUserLoading(false);
    } catch (error) {
      setErrorAlert(error);
      setFetchUserLoading(false);
    }
  };

  const hasRestrictedPlatformAccess = Boolean(
    !currentOrg?.isPaidUser &&
      gamificationProfile?.id &&
      !gamificationProfile?.unlockPremiumFeatures,
  );

  // new user that was created within the last 24 hours
  // createdAt is a Unix timestamp in seconds (float)
  const isNewUser =
    currentOrg?.createdAt &&
    (new Date().getTime() - currentOrg.createdAt * 1000) /
      (1000 * 60 * 60 * 24) <
      1;
  return {
    fetchUserInfo,
    fetchUserLoading,
    setFetchUserLoading,
    hasRestrictedPlatformAccess,
    isNewUser,
  };
};
